import React, { useState, useRef, useMemo } from "react";
import { toast } from "react-toastify";

import { useDispatch, useSelector } from "react-redux";

import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import MenuItem from "@material-ui/core/MenuItem";
import {
  type GridSelectionModel,
  type GridFilterModel,
} from "@material-ui/x-grid";

import TableHeader, { TableHeaderButton } from "src/components/TableHeader";
import TimeFilter, {
  Select,
  getPeriodFromPreset,
} from "src/components/TimeFilter";
import { useDateRange } from "src/utils/timeFilter";
import XGrid from "src/components/ConfiguredXGrid";
import CSVFormattedXGrid from "src/components/CSVFormattedXGrid";
import {
  createTableToolbar,
  DefaultFeatures,
} from "src/components/TableToolbar";
import ConfirmDialog from "src/components/ConfirmDialog";
import InfoTooltip from "src/components/InfoTooltip";
import { setFilter } from "src/store/inventory/actions";
import { inventoriesSelector } from "src/store/inventory/selector";
import { filteredExpensesSelector } from "src/store/transaction/selector";
import { effectiveUserTimeZoneSelector } from "src/store/system/selector";
import { METHOD_TYPE } from "src/enums";
import { deleteMappedTransactions } from "src/apiService/modules/mapped-transactions";
import {
  isSplitTransaction,
  notifySkippedSplitTransactions,
} from "../splitTransactions";
import vendorRenderCell from "src/utils/vendorRenderCell";
import renderHeaderDescriptionLabel from "src/utils/renderHeaderLabel";
import { useFullscreen } from "src/utils/fullscreen";
import { useRecordEvent } from "src/hooks/useRecordEvent";
import { calendarYmdInTimeZone } from "src/utils/timezone";
import { setActiveDialog } from "src/store/adminHtml/actions";
import makeRenderCell from "src/utils/makeRenderCell";
import { formatAbsCurrency } from "src/utils/formatter";
import { makeTransactionsStyle } from "src/utils/gridCellStyles";
import type { MappedTransaction } from "src/interfaces/transaction.interface";
import { withGridStringOperatorsWithNotContains } from "src/utils/gridStringFilters";

import { Columns as TransactionColumns } from "../Expenses";

import BulkEditDialog from "./BulkEditDialog";
import BulkEditTransactionsDialog from "../Expenses/BulkEditDialog";
import BulkEditPanel from "../Expenses/BulkEditPanel";

const TableToolbar = createTableToolbar({
  features: {
    ...DefaultFeatures,
    Fullscreen: true,
  },
  options: { fileName: "My Reseller Genie - Inventory" },
});

const tableColumnInfo = {
  location: "Where you have stored the item.",
  purchase_date: "The date that you purchased this item.",
  list_date: "The date that you listed this item for sale.",
  purchase_price: "The amount you paid for the inventory.",
  notes: "Enter any other information that you need in here.",
};

const renderHeaderLabel = (params) => {
  const tooltip = tableColumnInfo[params.field];
  return (
    <div className="MuiDataGrid-columnHeaderTitle">
      {params.colDef.headerName}
      {!!tooltip && <InfoTooltip size="small" text={tooltip} />}
    </div>
  );
};

const Columns = withGridStringOperatorsWithNotContains([
  {
    field: "id",
    hide: true,
    valueFormatter: ({ value }) => {
      if (typeof value === "number") return `id:${value}`;
      if (typeof value !== "string" || !value.match(/[^0-9]/))
        return `id:${value}`;
      return value;
    },
  },
  { field: "item_title", headerName: "Item Title", width: 200 },
  {
    field: "quantity",
    headerName: "Quantity",
    width: 120,
    valueGetter: (params) => params.value || 1,
  },
  { field: "department", headerName: "Department", width: 150 },
  { field: "category", headerName: "Category", width: 200 },
  { field: "sub_category", headerName: "Sub Category", width: 200 },
  {
    field: "vendor",
    headerName: "Vendor",
    width: 200,
    renderCell: vendorRenderCell,
    renderHeader: renderHeaderDescriptionLabel,
    description: "The vendor is where you purchased the item (ex. Goodwill).",
    valueGetter: (params) => params.row.vendorName || params.value || "",
  },
  { field: "brand", headerName: "Brand", width: 200 },
  {
    field: "location",
    headerName: "Location",
    width: 300,
    renderHeader: renderHeaderLabel,
  },
  {
    field: "sku",
    headerName: "SKU",
    width: 200,
    renderHeader: renderHeaderLabel,
  },
  {
    field: "purchase_price",
    headerName: "Purchase Price",
    width: 200,
    type: "number",
    renderHeader: renderHeaderLabel,
    renderCell: makeRenderCell({
      valueFormatter: formatAbsCurrency,
      style: makeTransactionsStyle,
    }),
  },
  {
    field: "purchase_date",
    headerName: "Purchase Date",
    width: 200,
    type: "date",
    renderHeader: renderHeaderLabel,
  },
  {
    field: "list_date",
    headerName: "List Date",
    width: 200,
    type: "date",
    renderHeader: renderHeaderLabel,
  },
  {
    field: "platforms_listed",
    headerName: "Platforms Listed",
    width: 300,
    renderHeader: renderHeaderLabel,
    valueFormatter: ({ value }) => {
      if (Array.isArray(value) && value.length) {
        return `${value.join(", ")}`;
      }
      return "";
    },
  },
  {
    field: "notes",
    headerName: "Notes",
    width: 300,
    renderHeader: renderHeaderLabel,
  },
]);

export default function Content() {
  const recordEvent = useRecordEvent();

  const dispatch = useDispatch();
  const timeZone = useSelector(effectiveUserTimeZoneSelector);
  const [view, setView] = useState("current-inventory-detail");
  const { timeOption: timeFilter, setDateRange } = useDateRange("transaction");

  const isInventoryTable = [
    "current-inventory-detail",
    "item-detail-attached",
    "item-detail-unattached",
  ].includes(view);
  const {
    items: initialItems,
    method,
    loading,
    // error,
    // limit,
    filter: filterModel,
    // selected: selectionModel,
  } = useSelector(inventoriesSelector);
  const items = useMemo(() => {
    if (view === "item-detail-attached")
      return initialItems.filter((i) => i.transaction_inventory_id);
    if (view === "item-detail-unattached")
      return initialItems.filter((i) => !i.transaction_inventory_id);
    return initialItems;
  }, [initialItems, view]);
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const filters = useMemo(() => {
    const models: GridFilterModel[] = [];
    if (isInventoryTable) return models;

    const period =
      timeFilter.type === "custom"
        ? timeFilter.period
        : getPeriodFromPreset(timeFilter, timeZone);

    const model = {
      items: [
        {
          columnField: "date",
          operatorValue: "onOrAfter",
          value: calendarYmdInTimeZone(period.start, timeZone),
        },
        {
          columnField: "date",
          operatorValue: "onOrBefore",
          value: calendarYmdInTimeZone(period.end, timeZone),
        },
      ],
    };

    models.push(model);
    return models;
  }, [isInventoryTable, timeFilter, timeZone]);

  const allTransactions = useSelector((state) =>
    filteredExpensesSelector(state, filters)
  );

  const transactions = useMemo(() => {
    if (view === "transactions-no-items-attached") {
      return allTransactions.filter(
        (transaction) =>
          transaction.accountDoc?.type?.[0] === "assets" &&
          transaction.accountDoc?.type?.[1] === "inventory" &&
          (!transaction.items || transaction.items.length === 0)
      );
    } else if (view === "transactions-items-attached") {
      return allTransactions.filter(
        (transaction) =>
          transaction.accountDoc?.type?.[0] === "assets" &&
          transaction.accountDoc?.type?.[1] === "inventory" &&
          transaction.items?.length > 0
      );
    }
    return allTransactions;
  }, [view, allTransactions]);

  const [dialog, setDialog] = useState<
    | { type: "delete" }
    | { type: "bulk-upload-inventory" }
    | { type: "bulk-upload-transactions" }
    | { type: "bulk-edit-panel" }
    | null
  >(null);

  const { editableTransactionIds, readOnlyCount } = useMemo(() => {
    const ids: string[] = [];
    let readOnly = 0;
    if (isInventoryTable)
      return { editableTransactionIds: ids, readOnlyCount: readOnly };
    for (const id of selectionModel) {
      const row = transactions.find((r) => r.id === id);
      if (!row) continue;
      if (row.read_only) {
        readOnly += 1;
      } else {
        ids.push(String(id));
      }
    }
    return { editableTransactionIds: ids, readOnlyCount: readOnly };
  }, [selectionModel, transactions, isInventoryTable]);
  const tableSectionRef = useRef<HTMLDivElement>();
  const requestFullscreen = useFullscreen();

  const handleSelectItem = (selectionModel: GridSelectionModel) => {
    setSelectionModel(selectionModel);
  };

  const handleDeleteSelected = async () => {
    setDialog(null);
    if (selectionModel.length > 0) {
      const rows = isInventoryTable ? items : transactions;
      let skippedSplit = 0;
      const toDelete = selectionModel.reduce((all, id) => {
        const item = rows.find((r) => r.id === id);
        if (item) {
          // Split transactions can't be bulk deleted; they must be
          // edited/deleted individually so the whole group is handled.
          if (isSplitTransaction(item)) {
            skippedSplit += 1;
            return all;
          }
          all.push({
            collection:
              item.collection ||
              (isInventoryTable ? "inventory" : "transaction"),
            collection_id: item.collection_id || item.id,
          });
        }
        return all;
      }, [] as Pick<MappedTransaction, "collection_id" | "collection">[]);

      if (toDelete.length) {
        await deleteMappedTransactions(toDelete);
        toast.success("Inventory item(s) deleted successfully!");
      }
      notifySkippedSplitTransactions(skippedSplit);
      setSelectionModel([]);
    }
  };

  return (
    <div>
      <TimeFilter
        value={timeFilter}
        onChange={(v) => setDateRange(v)}
        noTime={isInventoryTable}
      >
        <Select
          label="View"
          value={view}
          onChange={(e) => {
            setSelectionModel([]);
            const view = e.target.value as string;
            setView(view);
            setSelectionModel([]);
            recordEvent({
              type: "feature-adoption",
              feature: "transaction-details-view",
              element: "inventory",
              view: view,
            });
          }}
        >
          <MenuItem value="current-inventory-detail">
            Current Item Detail
          </MenuItem>
          <MenuItem value="item-detail-attached">
            Item Detail (Attached to Transaction)
          </MenuItem>
          <MenuItem value="item-detail-unattached">
            Item Detail (Unattached)
          </MenuItem>
          <MenuItem value="transactions-no-items-attached">
            Transactions (No Items Attached)
          </MenuItem>
          <MenuItem value="transactions-items-attached">
            Transactions (Items Attached)
          </MenuItem>
        </Select>
      </TimeFilter>
      <Box
        component={Paper}
        {...({ ref: tableSectionRef } as any)}
        display="flex"
        flexDirection="column"
        minHeight={730}
        height="auto"
        p={2.5}
      >
        <TableHeader title="Inventory Detail" hideChildrenInFullscreen>
          <TableHeaderButton
            color="primary"
            onClick={() =>
              setDialog(
                isInventoryTable
                  ? { type: "bulk-upload-inventory" }
                  : { type: "bulk-edit-panel" }
              )
            }
            disabled={
              !isInventoryTable &&
              (selectionModel.length === 0 ||
                editableTransactionIds.length === 0)
            }
            tooltip={
              !isInventoryTable
                ? selectionModel.length === 0
                  ? "Select transactions to bulk edit."
                  : editableTransactionIds.length === 0
                  ? "Beginning Balance journals can't be bulk edited."
                  : undefined
                : undefined
            }
          >
            {isInventoryTable
              ? "Bulk Edit Inventory"
              : "Bulk Edit Transactions"}
          </TableHeaderButton>
          <TableHeaderButton
            colorType="error"
            disabled={loading || selectionModel.length === 0}
            onClick={() => {
              setDialog({
                type: "delete",
              });
            }}
          >
            Delete Selected
          </TableHeaderButton>
        </TableHeader>
        {isInventoryTable ? (
          <XGrid
            checkboxSelection
            tableName="inventory"
            Component={CSVFormattedXGrid}
            rows={isInventoryTable ? items : transactions}
            columns={isInventoryTable ? Columns : TransactionColumns}
            headerHeight={35}
            rowHeight={35}
            loading={loading && method === METHOD_TYPE.LIST}
            components={{
              Toolbar: TableToolbar,
            }}
            componentsProps={{
              toolbar: {
                onFullscreenClick: () => {
                  if (document.fullscreenElement) {
                    document.exitFullscreen();
                    return;
                  }
                  if (!tableSectionRef.current) return;
                  requestFullscreen(tableSectionRef.current);
                  recordEvent({
                    type: "feature-adoption",
                    feature: "fullscreen",
                    element: "inventory-table",
                  });
                },
                onQuickFilterBlur: (e) => {
                  const text = e.target.value;
                  if (!text) return;
                  recordEvent({
                    type: "feature-adoption",
                    feature: "quick-filter",
                    element: "inventory",
                    text,
                  });
                },
              },
            }}
            filterModel={filterModel}
            selectionModel={selectionModel}
            onSelectionModelChange={handleSelectItem}
            onCellDoubleClick={(e) => {
              dispatch(setActiveDialog("edit_inventory", { id: e.row.id }));
            }}
            onFilterModelChange={(e) => {
              dispatch(setFilter(e));
              recordEvent({
                type: "feature-adoption",
                feature: "table-filter",
                element: "inventory",
                filter: JSON.stringify(e),
              });
            }}
          />
        ) : (
          <XGrid
            checkboxSelection
            onSelectionModelChange={handleSelectItem}
            tableName="inventory-transaction"
            Component={CSVFormattedXGrid}
            rows={isInventoryTable ? items : transactions}
            columns={isInventoryTable ? Columns : TransactionColumns}
            headerHeight={35}
            rowHeight={35}
            loading={loading && method === METHOD_TYPE.LIST}
            components={{
              Toolbar: TableToolbar,
            }}
            componentsProps={{
              toolbar: {
                onFullscreenClick: () => {
                  if (document.fullscreenElement) {
                    document.exitFullscreen();
                    return;
                  }
                  if (!tableSectionRef.current) return;
                  requestFullscreen(tableSectionRef.current);
                  recordEvent({
                    type: "feature-adoption",
                    feature: "fullscreen",
                    element: "inventory-table",
                  });
                },
                onQuickFilterBlur: (e) => {
                  const text = e.target.value;
                  if (!text) return;
                  recordEvent({
                    type: "feature-adoption",
                    feature: "quick-filter",
                    element: "inventory",
                    text,
                  });
                },
              },
            }}
            onFilterModelChange={(e: any) => {
              recordEvent({
                type: "feature-adoption",
                feature: "table-filter",
                element: "inventory",
                filter: JSON.stringify(e),
              });
            }}
            onCellDoubleClick={(e) => {
              if (e.row?.journal_entry_id) {
                dispatch(
                  setActiveDialog("add_journal", {
                    id: e.row.journal_entry_id,
                  })
                );
                return;
              }
              if (e.row?.split_group_id) {
                dispatch(
                  setActiveDialog("edit_split", {
                    split_group_id: e.row.split_group_id,
                    id: e.row.collection_id || e.row.id,
                  })
                );
                return;
              }
              if (e.row.collection === "transaction") {
                dispatch(
                  setActiveDialog("edit_transaction", {
                    id: e.row.collection_id,
                  })
                );
                return;
              }
              if (e.row.collection === "inventory") {
                dispatch(
                  setActiveDialog("edit_inventory", {
                    id: e.row.collection_id,
                  })
                );
                return;
              }
              if (e.row.collection === "sale") {
                dispatch(
                  setActiveDialog("edit_sale", {
                    id: e.row.collection_id,
                  })
                );
                return;
              }
              if (e.row?.type === "sale") {
                dispatch(
                  setActiveDialog("edit_sale", {
                    id: e.row.sale_id,
                  })
                );
                return;
              }
              dispatch(
                setActiveDialog("edit_transaction", {
                  id: e.row.id,
                })
              );
            }}
          />
        )}
      </Box>
      <BulkEditDialog
        open={dialog?.type === "bulk-upload-inventory"}
        onClose={() => setDialog(null)}
      />
      <BulkEditTransactionsDialog
        open={dialog?.type === "bulk-upload-transactions"}
        onClose={() => setDialog(null)}
      />
      <BulkEditPanel
        open={dialog?.type === "bulk-edit-panel"}
        onClose={() => setDialog(null)}
        selectedTransactionIds={editableTransactionIds}
        readOnlyCount={readOnlyCount}
        onOpenCSVBulkEdit={() =>
          setDialog({ type: "bulk-upload-transactions" })
        }
      />
      <ConfirmDialog
        open={dialog?.type === "delete"}
        title="Confirm"
        text={`Are you sure you want to delete ${selectionModel.length} inventory item(s)?`}
        onCancel={() => {
          setDialog(null);
        }}
        onConfirm={handleDeleteSelected}
      />
    </div>
  );
}
